<template>
  <div class="list-franchise-page">
    <!-- Notification -->
    <div v-if="notification" :class="['notification', notification.type]">
      <div class="notification-content">
        <span>{{ notification.message }}</span>
        <button @click="closeNotification" class="notification-close">
          <i class="fa fa-times"></i>
        </button>
      </div>
    </div>

    <div class="container">
      <div class="form-container">
        <!-- Back Link -->
        <router-link to="/" class="back-link">
          <i class="fa fa-arrow-left"></i>
          Back to Home
        </router-link>

        <!-- Form Card -->
        <div class="form-card">
          <!-- Header -->
          <div class="form-header">
            <h2>List Your Franchise</h2>
            <p>Fill in the details below to list your franchise opportunity</p>
          </div>
          
          <!-- Form Content -->
          <div class="form-content">
            <form @submit.prevent="handleSubmit" class="franchise-form">
              <!-- Basic Information -->
              <div class="form-section">
                <h3>Basic Information</h3>
                <div class="form-grid">
                  <div class="form-group">
                    <label for="name">Name *</label>
                    <input
                      id="name"
                      v-model="form.name"
                      type="text"
                      :class="{ 'error': errors.name }"
                      placeholder="Your full name"
                      @input="clearError('name')"
                    />
                    <span v-if="errors.name" class="error-message">{{ errors.name }}</span>
                  </div>

                  <div class="form-group">
                    <label for="phone">Phone Number *</label>
                    <input
                      id="phone"
                      v-model="form.phone"
                      type="tel"
                      :class="{ 'error': errors.phone }"
                      placeholder="10-digit mobile number"
                      @input="clearError('phone')"
                    />
                    <span v-if="errors.phone" class="error-message">{{ errors.phone }}</span>
                  </div>

                  <div class="form-group">
                    <label for="email">Email Address *</label>
                    <input
                      id="email"
                      v-model="form.email"
                      type="email"
                      :class="{ 'error': errors.email }"
                      placeholder="your@email.com"
                      @input="clearError('email')"
                    />
                    <span v-if="errors.email" class="error-message">{{ errors.email }}</span>
                  </div>

                  <div class="form-group">
                    <label for="designation">Designation *</label>
                    <input
                      id="designation"
                      v-model="form.designation"
                      type="text"
                      :class="{ 'error': errors.designation }"
                      placeholder="Your role in the company"
                      @input="clearError('designation')"
                    />
                    <span v-if="errors.designation" class="error-message">{{ errors.designation }}</span>
                  </div>
                </div>
              </div>

              <!-- Company Information -->
              <div class="form-section">
                <h3>Company Information</h3>
                <div class="form-grid">
                  <div class="form-group">
                    <label for="companyName">Company Name *</label>
                    <input
                      id="companyName"
                      v-model="form.companyName"
                      type="text"
                      :class="{ 'error': errors.companyName }"
                      placeholder="Your company name"
                      @input="clearError('companyName')"
                    />
                    <span v-if="errors.companyName" class="error-message">{{ errors.companyName }}</span>
                  </div>

                  <div class="form-group">
                    <label for="website">Website</label>
                    <input
                      id="website"
                      v-model="form.website"
                      type="url"
                      placeholder="https://www.example.com"
                    />
                  </div>

                  <div class="form-group">
                    <label for="headquarters">Headquarters *</label>
                    <input
                      id="headquarters"
                      v-model="form.headquarters"
                      type="text"
                      :class="{ 'error': errors.headquarters }"
                      placeholder="City, State"
                      @input="clearError('headquarters')"
                    />
                    <span v-if="errors.headquarters" class="error-message">{{ errors.headquarters }}</span>
                  </div>

                  <div class="form-group">
                    <label for="states">Operating States *</label>
                    <select
                      id="states"
                      v-model="form.states"
                      multiple
                      :class="{ 'error': errors.states }"
                      @change="clearError('states')"
                    >
                      <option v-for="state in indianStates" :key="state.id" :value="state.name">
                        {{ state.name }}
                      </option>
                    </select>
                    <span v-if="errors.states" class="error-message">{{ errors.states }}</span>
                    <p class="help-text">Hold Ctrl/Cmd to select multiple states</p>
                  </div>
                </div>
              </div>

              <!-- Franchise Details -->
              <div class="form-section">
                <h3>Franchise Details</h3>
                <div class="form-grid">
                  <div class="form-group">
                    <label for="category">Franchise Category *</label>
                    <select
                      id="category"
                      v-model="form.category"
                      :class="{ 'error': errors.category }"
                      @change="clearError('category')"
                    >
                      <option value="">Select Category</option>
                      <option v-for="cat in categories" :key="cat.id" :value="cat.slug || cat.name">
                        {{ cat.name }}
                      </option>
                    </select>
                    <span v-if="errors.category" class="error-message">{{ errors.category }}</span>
                  </div>

                  <div class="form-group">
                    <label>Investment Range (?) *</label>
                    <div class="investment-range">
                      <div class="range-input">
                        <input
                          v-model="form.investmentMin"
                          type="number"
                          :class="{ 'error': errors.investmentMin }"
                          placeholder="Min"
                          @input="clearError('investmentMin')"
                        />
                      </div>
                      <div class="range-input">
                        <input
                          v-model="form.investmentMax"
                          type="number"
                          :class="{ 'error': errors.investmentMax }"
                          placeholder="Max"
                          @input="clearError('investmentMax')"
                        />
                      </div>
                    </div>
                    <span v-if="errors.investmentMin || errors.investmentMax" class="error-message">
                      {{ errors.investmentMin || errors.investmentMax }}
                    </span>
                  </div>

                  <div class="form-group">
                    <label for="modelType">Franchise Model Type *</label>
                    <select
                      id="modelType"
                      v-model="form.modelType"
                      :class="{ 'error': errors.modelType }"
                      @change="clearError('modelType')"
                    >
                      <option value="">Select Model Type</option>
                      <option v-for="type in modelTypes" :key="type" :value="type">
                        {{ type }}
                      </option>
                    </select>
                    <span v-if="errors.modelType" class="error-message">{{ errors.modelType }}</span>
                  </div>
                </div>
              </div>

              <!-- Description -->
              <div class="form-section">
                <h3>Description</h3>
                <div class="form-group">
                  <label for="description">Brief Description *</label>
                  <textarea
                    id="description"
                    v-model="form.description"
                    :class="{ 'error': errors.description }"
                    rows="3"
                    placeholder="Describe your franchise opportunity, unique selling points, and what you're looking for in potential franchisees..."
                    @input="clearError('description')"
                  ></textarea>
                  <span v-if="errors.description" class="error-message">{{ errors.description }}</span>
                </div>
              </div>

              <!-- Form Actions -->
              <div class="form-actions">
                <router-link to="/" class="cancel-btn">Cancel</router-link>
                <button type="submit" :disabled="isSubmitting" class="submit-btn">
                  <div v-if="isSubmitting" class="loading-spinner"></div>
                  {{ isSubmitting ? 'Submitting...' : 'Submit Franchise Listing' }}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, reactive, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import api from '@/utils/api'
import { useCategoryStore } from '@/stores/categoryStore'
import { useStateStore } from '@/stores/stateStore'

const router = useRouter()
const categoryStore = useCategoryStore()
const stateStore = useStateStore()

// Form data
const form = reactive({
  name: '',
  phone: '',
  email: '',
  designation: '',
  companyName: '',
  website: '',
  headquarters: '',
  states: [],
  category: '',
  investmentMin: '',
  investmentMax: '',
  modelType: '',
  description: ''
})

// Form state
const errors = reactive({})
const isSubmitting = ref(false)
const notification = ref(null)



// Data
const indianStates = ref([])

const modelTypes = [
  'Single Unit',
  'Multi-Unit',
  'Master Franchise',
  'Area Development'
]

// Remove static categories array
const categories = ref([])

// Fetch categories and states from stores on mount
onMounted(async () => {
  try {
    // Fetch categories
    await categoryStore.fetchCategories()
    categories.value = categoryStore.categories

    
    // Fetch states
    await stateStore.fetchStates()
    indianStates.value = stateStore.getStates

  } catch (error) {

    // Fallback to static categories if API fails
    categories.value = [
      { id: 1, name: 'Food & Beverage', slug: 'food-beverage' },
      { id: 2, name: 'Retail', slug: 'retail' },
      { id: 3, name: 'Services', slug: 'services' },
      { id: 4, name: 'Health & Wellness', slug: 'health-wellness' },
      { id: 5, name: 'Education', slug: 'education' },
      { id: 6, name: 'Automobile', slug: 'automobile' }
    ]
  }
})

// Methods
const validateForm = () => {
  const newErrors = {}
  
  if (!form.name.trim()) newErrors.name = 'Name is required'
  if (!form.phone.trim()) newErrors.phone = 'Phone number is required'
  else if (!/^[0-9]{10}$/.test(form.phone)) newErrors.phone = 'Please enter a valid 10-digit phone number'
  
  if (!form.email.trim()) newErrors.email = 'Email is required'
  else if (!/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(form.email)) {
    newErrors.email = 'Please enter a valid email address'
  }
  
  if (!form.designation.trim()) newErrors.designation = 'Designation is required'
  if (!form.companyName.trim()) newErrors.companyName = 'Company name is required'
  if (!form.headquarters.trim()) newErrors.headquarters = 'Headquarters location is required'
  if (!form.states.length) newErrors.states = 'Please select at least one state'
  if (!form.category) newErrors.category = 'Please select a category'
  if (!form.investmentMin) newErrors.investmentMin = 'Minimum investment is required'
  else if (parseFloat(form.investmentMin) <= 0) newErrors.investmentMin = 'Must be greater than 0'
  
  if (!form.investmentMax) newErrors.investmentMax = 'Maximum investment is required'
  else if (parseFloat(form.investmentMax) <= 0) newErrors.investmentMax = 'Must be greater than 0'
  
  if (!form.modelType) newErrors.modelType = 'Please select a model type'
  if (!form.description.trim()) newErrors.description = 'Description is required'
  else if (form.description.length < 10) newErrors.description = 'Description must be at least 10 characters'
  
  Object.assign(errors, newErrors)
  return Object.keys(newErrors).length === 0
}

const clearError = (field) => {
  if (errors[field]) {
    delete errors[field]
  }
}



const showNotification = (type, message) => {
  notification.value = { type, message, isVisible: true }
  setTimeout(() => {
    notification.value = null
  }, 5000)
}

const closeNotification = () => {
  notification.value = null
}

const handleSubmit = async () => {
  if (!validateForm()) return
  isSubmitting.value = true
  try {
    // Prepare the data to send
    const franchiseData = {
      name: form.name.trim(),
      phone: form.phone.trim(),
      email: form.email.trim(),
      designation: form.designation.trim(),
      company_name: form.companyName.trim(),
      website: form.website.trim() || null,
      headquarters: form.headquarters.trim(),
      operating_states: form.states,
      category: form.category,
      investment_range: {
        min: parseFloat(form.investmentMin),
        max: parseFloat(form.investmentMax)
      },
      model_type: form.modelType,
      description: form.description.trim()
    }
    // Log the data being sent for debugging

    
    // Send POST request to /franchises
    await api.post('/listyourfranchise', franchiseData)
    showNotification('success', 'Franchise listing submitted successfully!')
    // Reset form
    Object.keys(form).forEach(key => {
      if (Array.isArray(form[key])) {
        form[key] = []
      } else {
        form[key] = ''
      }
    })
  } catch (error) {
    // Show more specific error messages
    let errorMessage = 'Failed to submit franchise listing. Please try again.'
    
    if (error.response) {
      // Server responded with error status
      if (error.response.data && error.response.data.message) {
        errorMessage = error.response.data.message
      } else if (error.response.status === 404) {
        errorMessage = 'API endpoint not found. Please check the server configuration.'
      } else if (error.response.status === 500) {
        errorMessage = 'Server error. Please try again later.'
      } else if (error.response.status === 422) {
        errorMessage = 'Validation error. Please check your form data.'
      }
    } else if (error.request) {
      // Request was made but no response received
      errorMessage = 'Network error: Cannot connect to server. Please check your internet connection.'
    } else {
      // Something else happened
      errorMessage = `Error: ${error.message}`
    }
    
    showNotification('error', errorMessage)
  } finally {
    isSubmitting.value = false
  }
}
</script>

<style scoped>
.list-franchise-page {
  min-height: 100vh;
  background: linear-gradient(to bottom, #f9fafb, #ffffff);
  padding: 96px 0 64px;
}

/* Notification */
.notification {
  position: fixed;
  top: 20px;
  right: 20px;
  z-index: 1000;
  padding: 16px 20px;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  max-width: 400px;
}

.notification.success {
  background: #dcfce7;
  color: #166534;
  border: 1px solid #bbf7d0;
}

.notification.error {
  background: #fef2f2;
  color: #dc2626;
  border: 1px solid #fecaca;
}

.notification-content {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.notification-close {
  background: none;
  border: none;
  color: inherit;
  cursor: pointer;
  padding: 4px;
  margin-left: 12px;
}

/* Container */
.container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 16px;
}

.form-container {
  max-width: 1024px;
  margin: 0 auto;
}

/* Back Link */
.back-link {
  display: inline-flex;
  align-items: center;
  color: #6b7280;
  text-decoration: none;
  margin-bottom: 24px;
  font-weight: 500;
  transition: color 0.2s;
}

.back-link:hover {
  color: #2563eb;
}

.back-link i {
  margin-right: 8px;
}

/* Form Card */
.form-card {
  background: white;
  border-radius: 12px;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
  overflow: hidden;
  border: 1px solid #f3f4f6;
}

/* Form Header */
.form-header {
  background: linear-gradient(to right, #2563eb, #1d4ed8);
  padding: 24px 32px;
  color: white;
}

.form-header h2 {
  font-size: 24px;
  font-weight: 700;
  margin-bottom: 8px;
}

.form-header p {
  color: #dbeafe;
  margin: 0;
}

/* Form Content */
.form-content {
  padding: 32px;
}

.franchise-form {
  display: flex;
  flex-direction: column;
  gap: 32px;
}

/* Form Sections */
.form-section h3 {
  font-size: 18px;
  font-weight: 600;
  color: #111827;
  margin-bottom: 16px;
}

.form-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 24px;
}

/* Form Groups */
.form-group {
  display: flex;
  flex-direction: column;
}

.form-group label {
  font-size: 14px;
  font-weight: 500;
  color: #374151;
  margin-bottom: 4px;
}

.form-group input,
.form-group select,
.form-group textarea {
  padding: 12px 16px;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-size: 16px;
  transition: all 0.2s;
}

.form-group input:focus,
.form-group select:focus,
.form-group textarea:focus {
  outline: none;
  border-color: #2563eb;
  box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.1);
}

.form-group input.error,
.form-group select.error,
.form-group textarea.error {
  border-color: #dc2626;
}

.error-message {
  color: #dc2626;
  font-size: 12px;
  margin-top: 4px;
}

.help-text {
  color: #6b7280;
  font-size: 12px;
  margin-top: 4px;
}

/* Investment Range */
.investment-range {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
}

.range-input input {
  width: 100%;
}



/* Form Actions */
.form-actions {
  display: flex;
  justify-content: flex-end;
  gap: 16px;
  padding-top: 24px;
  border-top: 1px solid #f3f4f6;
}

.cancel-btn {
  padding: 12px 24px;
  border: 1px solid #d1d5db;
  color: #374151;
  border-radius: 8px;
  text-decoration: none;
  font-weight: 500;
  transition: all 0.2s;
}

.cancel-btn:hover {
  background: #f9fafb;
}

.submit-btn {
  padding: 12px 24px;
  background: #2563eb;
  color: white;
  border: none;
  border-radius: 8px;
  font-weight: 500;
  cursor: pointer;
  transition: background 0.2s;
  display: flex;
  align-items: center;
  gap: 8px;
}

.submit-btn:hover:not(:disabled) {
  background: #1d4ed8;
}

.submit-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.loading-spinner {
  width: 20px;
  height: 20px;
  border: 2px solid transparent;
  border-top: 2px solid white;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

/* Responsive */
@media (max-width: 768px) {
  .form-content {
    padding: 24px 16px;
  }
  
  .form-header {
    padding: 20px 16px;
  }
  
  .form-grid {
    grid-template-columns: 1fr;
  }
  
  .investment-range {
    grid-template-columns: 1fr;
  }
  
  .form-actions {
    flex-direction: column;
  }
  
  .images-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (max-width: 480px) {
  .form-content {
    padding: 16px;
  }
  
  .form-header {
    padding: 16px;
  }
  
  .form-header h2 {
    font-size: 20px;
  }
  
  .images-grid {
    grid-template-columns: 1fr;
  }
}
</style> 